/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { getContentVisibilityAsync } from '../../actions/extensions/DataActionExtension.g';
import { getProductIdFromLink, IContentVisibility } from './actions/get-product-content-visibility.action';
import { IMoeV2IconLinkMenuData } from './moe-v2-icon-link-menu.data';
import { IListItemData, IMoeV2IconLinkMenuProps } from './moe-v2-icon-link-menu.props.autogenerated';

export interface IMoeV2IconLinkMenuViewProps extends IMoeV2IconLinkMenuProps<IMoeV2IconLinkMenuData> {
    hiddenProductIds: number[];
    productVisibilityReady: boolean;
}

interface IMoeV2IconLinkMenuState {
    hiddenProductIds: number[];
    productVisibilityReady: boolean;
}

/**
 *
 * MoeV2IconLinkMenu component
 * @extends {React.PureComponent<IMoeV2IconLinkMenuProps<IMoeV2IconLinkMenuData>>}
 */
class MoeV2IconLinkMenu extends React.PureComponent<IMoeV2IconLinkMenuProps<IMoeV2IconLinkMenuData>, IMoeV2IconLinkMenuState> {
    private _isMounted = false;

    private _visibilityRequestId = 0;

    public constructor(props: IMoeV2IconLinkMenuProps<IMoeV2IconLinkMenuData>) {
        super(props);

        // Use the server resolved data action result when it is already available so that the
        // server rendered markup is filtered and no unreleased item flashes before hydration.
        const resolved = this._getResolvedVisibility(props);
        this.state = {
            hiddenProductIds: resolved || [],
            productVisibilityReady: resolved !== undefined || !this._hasProductLinks(props)
        };
    }

    public componentDidMount(): void {
        this._isMounted = true;
        if (this._getResolvedVisibility(this.props) === undefined) {
            this._setVisibilityState(this.props);
        }
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
        this._visibilityRequestId++;
    }

    public componentDidUpdate(prevProps: IMoeV2IconLinkMenuProps<IMoeV2IconLinkMenuData>): void {
        if (prevProps.config !== this.props.config) {
            this._setVisibilityState(this.props);
        }
    }

    public render(): JSX.Element | null {
        return this.props.renderView({
            ...this.props,
            hiddenProductIds: this.state.hiddenProductIds,
            productVisibilityReady: this.state.productVisibilityReady
        });
    }

    private _setVisibilityState(props: IMoeV2IconLinkMenuProps<IMoeV2IconLinkMenuData>): void {
        const requestId = ++this._visibilityRequestId;
        const productIds = this._getProductIds(props.config.listItems || []);
        if (productIds.length === 0) {
            this.setState({ hiddenProductIds: [], productVisibilityReady: true });
            return;
        }

        const actionContext = props.context?.actionContext;
        if (!actionContext) {
            this.setState({ hiddenProductIds: [], productVisibilityReady: true });
            return;
        }

        this.setState({ productVisibilityReady: false });

        const apiSettings = props.context?.request?.apiSettings;
        const catalogId = Number(apiSettings?.catalogId || 0);

        const timeoutPromise: Promise<IContentVisibility[]> = new Promise(resolve => setTimeout(() => resolve([]), 8000));
        const visibilityPromise = Promise.race([
            getContentVisibilityAsync({ callerContext: actionContext, bypassCache: 'get' }, productIds.join(','), catalogId),
            timeoutPromise
        ]) as Promise<IContentVisibility[]>;

        void visibilityPromise
            .then(result => {
                if (!this._isMounted || requestId !== this._visibilityRequestId) {
                    return;
                }

                const hiddenProductIds = (result || [])
                    .filter((visibility: IContentVisibility) => visibility.IsVisible === false && visibility.RecordId !== undefined)
                    .map((visibility: IContentVisibility) => visibility.RecordId as number);

                this.setState({ hiddenProductIds, productVisibilityReady: true });
            })
            .catch(() => {
                if (!this._isMounted || requestId !== this._visibilityRequestId) {
                    return;
                }

                this.setState({ hiddenProductIds: [], productVisibilityReady: true });
            });
    }

    private _hasProductLinks(props: IMoeV2IconLinkMenuProps<IMoeV2IconLinkMenuData>): boolean {
        return this._getProductIds(props.config.listItems || []).length > 0;
    }

    private _getResolvedVisibility(props: IMoeV2IconLinkMenuProps<IMoeV2IconLinkMenuData>): number[] | undefined {
        const asyncResult = (props.data?.productContentVisibility as unknown) as {
            result?: { visibility?: IContentVisibility[] };
        };
        const visibility = asyncResult?.result?.visibility;
        if (!Array.isArray(visibility) || visibility.length === 0) {
            return undefined;
        }

        // Only trust the server result when it covers every product on the module. A partial result means
        // some links were still unresolved during server rendering, and trusting it would leave the missing
        // products permanently unchecked and therefore visible.
        const expectedProductIds = this._getProductIds(props.config.listItems || []);
        const coveredProductIds = new Set(visibility.map((entry: IContentVisibility) => entry.RecordId));
        if (expectedProductIds.some(productId => !coveredProductIds.has(productId))) {
            return undefined;
        }

        return visibility
            .filter((entry: IContentVisibility) => entry.IsVisible === false && entry.RecordId !== undefined)
            .map((entry: IContentVisibility) => entry.RecordId as number);
    }

    private _getProductIds(items: IListItemData[]): number[] {
        return Array.from(
            new Set(items.map(item => getProductIdFromLink(item.link)).filter((productId): productId is number => productId !== undefined))
        );
    }
}

export default MoeV2IconLinkMenu;
